import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { api } from "@/lib/api";
import { cachedApi, invalidateCache } from "@/lib/cachedApi";
import { Sparkles, ChevronRight, X, AlertTriangle, TrendingUp, Lightbulb, Info } from "lucide-react";

const TYPE_CFG = {
  alert:    { icon: AlertTriangle, cls: "text-rose-600 dark:text-rose-400",    bg: "bg-rose-50 dark:bg-rose-500/10 border-rose-200 dark:border-rose-500/20" },
  warning:  { icon: AlertTriangle, cls: "text-amber-600 dark:text-amber-400",  bg: "bg-amber-50 dark:bg-amber-500/10 border-amber-200 dark:border-amber-500/20" },
  positive: { icon: TrendingUp,    cls: "text-emerald-600 dark:text-emerald-400", bg: "bg-emerald-50 dark:bg-emerald-500/10 border-emerald-200 dark:border-emerald-500/20" },
  tip:      { icon: Lightbulb,     cls: "text-sky-600 dark:text-sky-400",      bg: "bg-sky-50 dark:bg-sky-500/10 border-sky-200 dark:border-sky-500/20" },
};

// `compact`: versão do painel lateral do Intelligence — sem moldura própria
// (o painel já tem cabeçalho), mostra a ação de cada insight e um estado vazio.
export default function InsightsWidget({ compact = false }) {
  const [insights, setInsights] = useState([]);
  const [loaded, setLoaded] = useState(false);

  const load = () => {
    cachedApi("oplyx_insights_v1", () => api.get("/insights/"), 30 * 60 * 1000)
      .then(data => { setInsights(data.insights || []); setLoaded(true); })
      .catch(() => setLoaded(true));
  };

  useEffect(load, []);

  const dismiss = async (id) => {
    setInsights(prev => prev.filter(i => i.id !== id));
    try { await api.post("/insights/dismiss", { insight_id: id, days: 7 }); } catch {}
    invalidateCache("oplyx_insights_v1");
  };

  if (compact) {
    if (!loaded) return <div className="text-xs text-neutral-400 px-1 py-2">Carregando…</div>;
    if (insights.length === 0) {
      return (
        <div className="text-xs text-neutral-500 dark:text-neutral-400 leading-relaxed px-1 py-2">
          Nada pedindo atenção agora. Os insights são recalculados todo dia às 7h.
        </div>
      );
    }
    return (
      <div className="space-y-2" data-testid="intel-panel-insights">
        {insights.slice(0, 5).map(insight => {
          const cfg = TYPE_CFG[insight.type] || TYPE_CFG.tip;
          const Icon = cfg.icon;
          return (
            <div key={insight.id} className={`rounded-lg border px-2.5 py-2 ${cfg.bg}`}>
              <div className="flex items-start gap-2">
                <Icon className={`w-3.5 h-3.5 mt-0.5 shrink-0 ${cfg.cls}`} />
                <div className="flex-1 min-w-0">
                  <div className="text-xs font-semibold text-neutral-800 dark:text-neutral-200 leading-tight">{insight.title}</div>
                  <div className="text-[11px] text-neutral-500 dark:text-neutral-400 mt-0.5 leading-snug line-clamp-3">{insight.body}</div>
                  {insight.action_url && (
                    <Link to={insight.action_url} className={`inline-flex items-center gap-0.5 text-[11px] mt-1 font-medium ${cfg.cls} hover:underline`}>
                      {insight.action} <ChevronRight className="w-3 h-3" />
                    </Link>
                  )}
                </div>
                <button
                  onClick={() => dismiss(insight.id)}
                  className="text-neutral-400 hover:text-neutral-600 dark:hover:text-neutral-200 p-0.5 rounded shrink-0 transition-colors"
                  title="Dispensar por 7 dias"
                >
                  <X className="w-3 h-3" />
                </button>
              </div>
            </div>
          );
        })}
      </div>
    );
  }

  if (!loaded || insights.length === 0) return null;

  // Show top 3 insights only in widget
  const visible = insights.slice(0, 3);

  return (
    <div className="rounded-xl border border-neutral-200/70 dark:border-white/[0.05] bg-white dark:bg-[#121417] p-5 space-y-3">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-[#00FF88]" />
          <span className="text-sm font-semibold">Insights da semana</span>
          {insights.length > 0 && (
            <span className="text-[10px] bg-[#00FF88]/20 text-[#00FF88] font-bold rounded-full px-1.5 py-0.5">
              {insights.length}
            </span>
          )}
        </div>
        <Link to="/insights" className="flex items-center gap-1 text-xs text-neutral-500 hover:text-neutral-900 dark:hover:text-white transition-colors">
          Ver todos <ChevronRight className="w-3 h-3" />
        </Link>
      </div>

      {visible.map(insight => {
        const cfg = TYPE_CFG[insight.type] || TYPE_CFG.tip;
        const Icon = cfg.icon;
        return (
          <div key={insight.id} className={`rounded-lg border px-3 py-2.5 ${cfg.bg} flex items-start gap-3`}>
            <Icon className={`w-4 h-4 mt-0.5 shrink-0 ${cfg.cls}`} />
            <div className="flex-1 min-w-0">
              <div className="text-sm font-medium text-neutral-800 dark:text-neutral-200 leading-tight">{insight.title}</div>
              <div className="text-xs text-neutral-500 dark:text-neutral-400 mt-0.5 line-clamp-2">{insight.body}</div>
            </div>
            <button
              onClick={() => dismiss(insight.id)}
              className="text-neutral-600 hover:text-neutral-400 p-0.5 rounded shrink-0 transition-colors"
              title="Dispensar por 7 dias"
            >
              <X className="w-3.5 h-3.5" />
            </button>
          </div>
        );
      })}
    </div>
  );
}
